import React from 'react';
import { MetricCard } from '../components/MetricCard';
import { DonutChart } from '../components/DonutChart';
import { BarChart } from '../components/BarChart';
import { DataSourceIndicator } from '../components/DataSourceIndicator';
import { useMultiRetailerData } from '../hooks/useMultiRetailerData';

/**
 * OverviewPage displays aggregated metrics and comparisons across all retailers.
 * Features include:
 * - Cross-retailer summary metrics
 * - Units and sales distribution by retailer 
 * - ROAS comparison across retailers
 * - Daily performance trends with retailer breakdowns
 */
export const OverviewPage: React.FC = () => {
  const { data, loading, error } = useMultiRetailerData();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500 text-lg">Loading overview data…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-2">Failed to load overview data</p>
          <p className="text-gray-500">
            {error || 'Please check your API connection and try again.'}
          </p>
        </div>
      </div>
    );
  }

  const {
    aggregated_summary,
    units_by_retailer,
    sales_by_retailer,
    roas_by_retailer,
    daily_sales_by_retailer,
  } = data;

  // Prepare data for ROAS horizontal bar chart
  const roasLabels = roas_by_retailer.map((item) => item.retailer_name);
  const roasValues = roas_by_retailer.map((item) => item.roas);

  // Prepare data for daily sales stacked bar chart (handle empty array)
  const dailyLabels = daily_sales_by_retailer.length > 0 
    ? daily_sales_by_retailer.map((item) =>
        new Date(item.date).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
        }),
      )
    : [];

  // Retailer colors for consistent theming
  const RETAILER_COLORS: Record<string, string> = {
    'Walmart': '#004c91',    // Walmart blue
    'Albertsons': '#ff9900', // Albertsons orange  
    'Kroger': '#004990',     // Kroger blue
    'Instacart': '#43b02a',  // Instacart green
  };

  // Get unique retailer names from daily data (handle empty array)
  const retailerNames = daily_sales_by_retailer.length > 0 
    ? Object.keys(daily_sales_by_retailer[0].values)
    : Object.keys(RETAILER_COLORS); // Fallback to known retailers

  // Construct datasets for daily sales stacked bar chart
  const dailySalesDatasets = retailerNames.map((retailer) => ({
    label: retailer,
    data: daily_sales_by_retailer.map((day) => day.values?.[retailer] ?? 0),
    backgroundColor: RETAILER_COLORS[retailer] ?? '#3b82f6',
  }));

  // Transform data for DonutChart (units by retailer)
  const unitsChartData = units_by_retailer.map((item) => ({
    brand_name: item.retailer_name,
    units: item.units,
    percentage: item.percentage,
  }));

  // Transform data for DonutChart (sales by retailer) 
  const salesChartData = sales_by_retailer.map((item) => ({
    brand_name: item.retailer_name,
    units: item.sales, // DonutChart uses 'units' property for values
    percentage: item.percentage,
  }));

  return (
    <div className="min-h-screen p-4 md:p-8 space-y-6" data-dashboard>
      {/* Data Source Indicator */}
      <div className="flex justify-end">
        <DataSourceIndicator source="api" />
      </div>
      
      {/* Aggregated Metrics Row */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total Sales"
          value={`$${(aggregated_summary.sales / 1000000).toFixed(1)}M`}
        />
        <MetricCard
          label="Total Units"
          value={`${(aggregated_summary.units / 1000).toFixed(0)}K`}
        />
        <MetricCard
          label="Total Spend"
          value={`$${(aggregated_summary.spend / 1000).toFixed(0)}K`}
        />
        <MetricCard 
          label="Avg ROAS" 
          value={aggregated_summary.roas.toFixed(2)} 
        />
      </section>

      {/* Retailer Distribution Charts Row */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* ROAS by Retailer (Horizontal Bar) */}
        <div>
          <BarChart
            labels={roasLabels}
            datasets={[
              {
                label: 'ROAS',
                data: roasValues,
                backgroundColor: roasLabels.map(retailer => RETAILER_COLORS[retailer] ?? '#3b82f6'),
              },
            ]}
            title="ROAS by Retailer"
            stacked={false}
            horizontal={true}
            showLegend={false}
          />
        </div>

        {/* Units by Retailer (Donut) */}
        <div>
          <DonutChart 
            data={unitsChartData} 
            title="Units by Retailer"
          />
        </div>

        {/* Sales by Retailer (Donut) */}
        <div>
          <DonutChart 
            data={salesChartData} 
            title="Sales by Retailer" 
          />
        </div>
      </section>

      {/* Daily Performance Trends */}
      <section className="grid grid-cols-1 gap-4">
        {/* Daily Sales by Retailer (Stacked Bar) */}
        <div>
          <BarChart
            labels={dailyLabels}
            datasets={dailySalesDatasets}
            title="Daily Sales by Retailer"
            stacked={true}
            horizontal={false}
            showLegend={true}
          />
        </div>
      </section>

      {/* Retailer Summary Cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {units_by_retailer.map((retailer) => {
          const salesData = sales_by_retailer.find(s => s.retailer_name === retailer.retailer_name);
          const roasData = roas_by_retailer.find(r => r.retailer_name === retailer.retailer_name);
          
          return (
            <div 
              key={retailer.retailer_name}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border-l-4"
              style={{ borderLeftColor: RETAILER_COLORS[retailer.retailer_name] ?? '#3b82f6' }}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {retailer.retailer_name}
                </h3>
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: RETAILER_COLORS[retailer.retailer_name] ?? '#3b82f6' }}
                ></div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Units:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {(retailer.units / 1000).toFixed(1)}K ({retailer.percentage}%)
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Sales:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    ${salesData ? (salesData.sales / 1000).toFixed(0) : 0}K
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">ROAS:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {roasData?.roas.toFixed(2) ?? '0.00'}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
};